/**
 * Sign-In Page Route
 *
 * Implements the Microsoft Entra SSO authentication page with:
 * - ConEx logo and branding
 * - "Sign in with Microsoft" button
 * - MSAL popup integration
 * - Loading states during authentication
 * - Automatic redirect to /home after successful login
 * - Support for redirect query parameter
 *
 * Route Configuration:
 * - Path: /signin
 * - Public: isSecured: false (no auth required)
 */

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getCookieActive } from '@/lib/cookieManager'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { LogoWithText } from '@/components/logo'
import { Loader2 } from 'lucide-react'

export const Route = createFileRoute('/signin')({
  staticData: {
    title: 'Sign In',
    isSecured: false,
  },
  component: SignInPage,
})

interface SignInSearch {
  redirect?: string
}

function SignInPage() {
  const navigate = useNavigate()
  const { login, isUserLoggedIn } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem('conExRememberMe') === '1'
  })
  const search = useSearch({ from: Route.fullPath }) as SignInSearch

  // Redirect if already logged in — but only if the session cookie is still alive.
  // Without the cookie check, a stale in-memory isUserLoggedIn:true (which persists
  // across SPA navigations even after storage is cleared on expiry) would bounce the
  // user straight back to /home with no valid JWT, causing a blank screen.
  useEffect(() => {
    if (isUserLoggedIn && getCookieActive()) {
      navigate({
        to: search?.redirect || '/home',
        replace: true,
      })
    }
  }, [isUserLoggedIn, navigate, search?.redirect])

  const handleSignInClick = async () => {
    setIsLoading(true)
    try {
      // Store redirect URL before OAuth flow (it gets lost during MSAL redirect)
      if (search?.redirect) {
        sessionStorage.setItem('auth-redirect-url', search.redirect)
      }
      await login({ rememberMe })
      // Redirect is handled by useEffect above when isUserLoggedIn changes
    } catch {
      // Error is handled in AuthContext and toast is shown
      setIsLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="flex flex-col items-center gap-8 w-full max-w-md px-4">
        {/* ConEx Logo */}
        <div className="flex justify-center">
          <LogoWithText height="h-16" />
        </div>

        {/* Heading */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Welcome to ConEx 360</h1>
          <p className="mt-3 text-gray-600">Sign in with your Outlook Trilogy credentials</p>
        </div>

        {/* Microsoft Sign-In Button */}
        <Button
          onClick={handleSignInClick}
          disabled={isLoading}
          size="lg"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-all duration-200"
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Signing in...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              {/* Microsoft Logo SVG */}
              <svg
                className="h-5 w-5"
                viewBox="0 0 21 21"
                fill="currentColor"
              >
                <rect x="1" y="1" width="9" height="9" fill="white" />
                <rect x="11" y="1" width="9" height="9" fill="white" />
                <rect x="1" y="11" width="9" height="9" fill="white" />
                <rect x="11" y="11" width="9" height="9" fill="white" />
              </svg>
              <span>Sign in with Microsoft</span>
            </div>
          )}
        </Button>

        <div className="flex w-full items-center gap-2">
          <Checkbox
            id="remember-me"
            checked={rememberMe}
            onCheckedChange={(checked) => setRememberMe(checked === true)}
            disabled={isLoading}
          />
          <Label htmlFor="remember-me" className="text-sm text-gray-600 cursor-pointer">
            Remember me on this browser
          </Label>
        </div>

        {/* Help Text */}
        <p className="text-center text-sm text-gray-500 mt-4">
          @trilogynet.com accounts only
        </p>
      </div>
    </div>
  )
}
